import testHistoryApi from "../../api/testHistory"
import testHistoryHub from "../../api/hubs/testHistoryHub"
import { formatDate, formatCountdown } from "../../utils/dateTimeFormatter"

export default {
    state: {
        _testExpirationMessages: [],
        testKey: null,
        _testInfo: [],
        _testHistory: [],
        _recentTestHistory: [],
        _testResult: [],
        showNotReceived: false
    },
    getters: {
        isAllZonesTest: state => state.testKey && state.testKey.endsWith('Y0'),
        testExpirationMessages: (state, getters) => date => `Test expiring in ${formatCountdown(date - getters.dateNow)}. Click here to take actions.`,
        testInfo: (state, getters) => state._testInfo
            .map(el => {
                return {
                    effective: formatDate(el.EffectiveDate, "MMM D, YYYY, hh:mm:ss A"),
                    expire: formatDate(el.ExpireDate, "MMM D, YYYY, hh:mm:ss A"),
                    testCategory: el.TestCategoryDescription,
                    contactId: el.UserId,
                    isActive: el.IsTestActive,
                    testKey: el.TestKey
                }
            }),
        testHistory: state => state._testHistory
            .map(el => {
                return {
                    seqNo: el.SeqNo,
                    description: el.ZoneId && el.ZoneDescription ? el.ZoneId + ' - ' + el.ZoneDescription : el.ZoneDescription || el.ZoneId || el.ZoneList || 'All Zones',
                    eventDate: formatDate(el.EventDateTime, "MM/DD/YYYY hh:mm:ss A"),
                    csNo: el.CsNo,
                    event: el.EventId && el.EventId !== "CHTEST" && el.EventId !== "ONTEST" && el.EventId !== "CLTEST" && el.Event ? el.EventId + ' - ' + el.Event : el.Event || el.EventId || ''
                }
            }),
        activityDetails: state => seqNo => state._testHistory.filter(t => t.SeqNo === seqNo).map(
            el => {
                return {
                    eventDate: formatDate(el.EventDateTime, "MMM D, YYYY"),
                    eventTime: formatDate(el.EventDateTime, "hh:mm:ss A"),
                    csNo: el.CsNo,
                    event: el.EventId && el.Event ? el.EventId + " - " + el.Event : el.EventId || el.Event,
                    zone: el.ZoneId && el.ZoneDescription ? el.ZoneId + " - " + el.ZoneDescription : el.ZoneId || el.ZoneDescription,
                    user: el.User,
                    area: el.Area,
                    match: el.Match,
                    rawSignal: el.RawSignal,
                    comment: el.Comment
                }
            })[0] || {},
        recentTestHistory: state => state._recentTestHistory
            .map(el => {
                return {
                    seqNo: el.SeqNo,
                    text: el.ZoneDescription || el.ZoneId || el.Event,
                    eventType: el.EventType
                }
            }),
        testResult: state => state._testResult
            .filter(el => !state.showNotReceived || !el.TestPassed)
            .map(el => {
                return {
                    zone: el.ZoneId && el.Comment ? el.ZoneId + ' - ' + el.Comment : el.ZoneId || el.Comment,
                    csNo: el.CsNo,
                    passed: el.TestPassed
                }
            }),
        currentTest: state => state._testInfo.firstOrDefault(el => el.TestKey === state.testKey),
        currentTestHistoryRange: state => state._testInfo
            .filter(el => el.TestKey === state.testKey)
            .map(el => formatDate(el.EffectiveDate, "MMM D, YYYY, hh:mm:ss A") + " — " + formatDate(el.ExpireDate, "MMM D, YYYY, hh:mm:ss A"))[0]
    },
    mutations: {
        setTestKey: (state, key) => state.testKey = key,
        setTestHistory: (state, history) => state._testHistory = history,
        setTestResult: (state, result) => state._testResult = result,
        setTestInfo: (state, info) => state._testInfo = info,
        setShowNotReceived: (state, val) => state.showNotReceived = val,
        addToTestHistory: (state, events) => {
            for (var i = events.length - 1; i >= 0; i--) {
                state._testHistory.unshift(events[i]);
            }
        },
        updateTestResult: (state, zoneEvents) => {
            if (zoneEvents.length) {
                state._testResult.forEach(el =>
                    el.TestPassed = el.TestPassed || zoneEvents.some(z => z.CsNo === el.CsNo && z.ZoneId === el.ZoneId))
            }
        },
        addToRecentTestHistory: (state, event) => state._recentTestHistory.unshift(event),
        removeFromRecentTestHistory: (state, seqNo) => state._recentTestHistory = state._recentTestHistory.filter(e => e.SeqNo !== seqNo)
    },
    actions: {
        async initTestInfo({ commit }) {
            var info = await testHistoryApi.getTestInfo();
            commit('setTestInfo', info);
        },
        async checkTestInfo({ state, dispatch }) {
            if (state._testInfo.every(el => el.TestKey !== state.testKey)) {
                await dispatch("initTestInfo");
            }
        },
        async initTestHistory({ commit, state }) {
            var result = await testHistoryApi.getTestHistory(state.testKey);
            commit('setTestHistory', result);
        },
        async initTestHistoryDirty({ dispatch, state }) {
            if (state._testHistory.length) {
                return;
            }
            await dispatch('initTestHistory');
        },
        async initTestResult({ commit, state }) {
            var result = await testHistoryApi.getTestResult(state.testKey);
            commit('setTestResult', result);
        },
        async startTestHistoryMonitoring(store) {
            var handlers = {
                test: (...data) => _addTestHistoryEvents(data, store)
            }
            await testHistoryHub.startConnection(handlers);
        },
        async stopTestHistoryMonitoring() {
            await testHistoryHub.stopConnection();
        }
    }
}

function _addTestHistoryEvents(data, { state, commit }) {
    if (!data) {
        return;
    }

    var eventsArray = [];
    if(!Array.isArray(data)){
        eventsArray.push(data);
    } else{
        eventsArray = data;
    }

    const shownSeqNo = state._testHistory.map(it => it.SeqNo);
    const eventsToShow = eventsArray.filter(it => it.TestKey === state.testKey && shownSeqNo.indexOf(it.SeqNo) === -1);
    
    if (eventsToShow.length) {
        for (var i = eventsToShow.length - 1; i >= 0; i--) {
            commit("addToRecentTestHistory", eventsToShow[i]);
            let seqNo = eventsToShow[i].SeqNo;
            setTimeout(function () {
                commit("removeFromRecentTestHistory", seqNo)
            }, 10000)
        }

        commit("addToTestHistory", eventsToShow);

        const zoneEvents = eventsToShow.filter(ev => ev.ZoneId);
        if (zoneEvents.length) {
            commit("updateTestResult", zoneEvents);
        }
    }
}

